// Aba "Exemplos de redação" — biblioteca de propostas/relatórios que o Dwight
// consulta ao redigir. Suporta criar/editar individuais + import em lote de PDFs.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Alert, Tag, Typography, Space, Flex, App as AntApp, Empty, Radio,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDateTime } from './utils';
import ModalExemplo from './ModalExemplo';
import ModalBatchImport from './ModalBatchImport';
import { CATEGORIAS } from './types';
import type { ExemploItem } from './types';

const { Text } = Typography;

export default function ExemplosTab() {
  const { modal } = AntApp.useApp();
  const [items, setItems] = useState<ExemploItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<string>('todas');
  const [editando, setEditando] = useState<ExemploItem | null>(null);
  const [criando, setCriando] = useState(false);
  const [batchAberto, setBatchAberto] = useState(false);

  useEffect(() => { carregar(); }, []);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/agentes/exemplos`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }
  function excluir(id: string) {
    modal.confirm({
      title: 'Apagar este exemplo?', content: 'O Dwight deixará de usá-lo como referência.',
      okText: 'Apagar', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        const token = await getToken();
        await axios.delete(`${API_URL}/agentes/exemplos/${id}`, { headers: { Authorization: token } });
        await carregar();
      },
    });
  }
  const filtrados = filtro === 'todas' ? items : items.filter(i => i.categoria === filtro);

  return (
    <div>
      <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 12 }}>
        <Radio.Group value={filtro} onChange={(e) => setFiltro(e.target.value)} size="small">
          <Radio.Button value="todas">Todas ({items.length})</Radio.Button>
          {CATEGORIAS.map(c => (
            <Radio.Button key={c} value={c}>{c} ({items.filter(i => i.categoria === c).length})</Radio.Button>
          ))}
        </Radio.Group>
        <Space style={{ marginLeft: 'auto' }}>
          <Button type="primary" ghost icon={<UploadOutlined />} onClick={() => setBatchAberto(true)}>
            Importar PDFs em lote
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCriando(true)}>
            Adicionar exemplo
          </Button>
        </Space>
      </Flex>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message={
          <Text style={{ fontSize: 11 }}>
            💡 O Dwight consulta exemplos da categoria ao redigir nova proposta. Quanto mais exemplos por área, mais consistente o tom.
          </Text>
        } />
      {loading ? <Card loading /> : filtrados.length === 0 ? (
        <Empty description="Nenhum exemplo nesta categoria." />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {filtrados.map(e => (
            <Card key={e.id} size="small">
              <Flex align="center" gap={8} style={{ marginBottom: 4 }}>
                <Tag color="blue">{e.categoria}</Tag>
                <Text strong style={{ flex: 1 }}>{e.titulo}</Text>
                {e.pdf_origem && (
                  <Text title={`Importado de: ${e.pdf_origem}`} style={{ fontSize: 10, color: '#0930D0' }}>📎</Text>
                )}
                <Text type="secondary" style={{ fontSize: 10 }}>{fmtDateTime(e.atualizado_em)}</Text>
              </Flex>
              <Card size="small" style={{ background: '#FAFBFF', maxHeight: 80, overflow: 'hidden' }} bodyStyle={{ padding: 8 }}>
                <Text style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
                  {e.conteudo.slice(0, 280)}{e.conteudo.length > 280 ? '…' : ''}
                </Text>
              </Card>
              {Array.isArray(e.tags) && e.tags.length > 0 && (
                <Space size={4} wrap style={{ marginTop: 6 }}>
                  {e.tags.map((t, i) => <Tag key={i} color="gold">{t}</Tag>)}
                </Space>
              )}
              <Flex gap={6} style={{ marginTop: 8 }}>
                <Button size="small" type="primary" ghost icon={<EditOutlined />} onClick={() => setEditando(e)}>Editar</Button>
                <Button size="small" danger icon={<DeleteOutlined />} style={{ marginLeft: 'auto' }} onClick={() => excluir(e.id)} />
              </Flex>
            </Card>
          ))}
        </Space>
      )}
      {(editando || criando) && (
        <ModalExemplo item={editando}
          onClose={() => { setEditando(null); setCriando(false); }}
          onSalvo={() => { setEditando(null); setCriando(false); carregar(); }} />
      )}
      {batchAberto && (
        <ModalBatchImport onClose={() => setBatchAberto(false)} onConcluido={() => carregar()} />
      )}
    </div>
  );
}
